import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { compact } from "lodash/fp";

import * as Events from "@app/analytics/tracking/events";
import apiClient from "@app/common/api/apiClient";
import useAuth from "@app/common/auth/useAuth";
import useFeatureAccess from "@app/common/hooks/useFeatureAccess";

export const getQueryKey = (billingAccountId?: string) =>
  compact(["billing", "paymentMethod", billingAccountId]);

const usePaymentMethod = () => {
  // use the billing account straight off the user; this hook is currently being used
  // inside the billing context, so we can't use the one coming out of there without
  // causing issues re: circular dependencies
  const { user } = useAuth();
  const billingAccount = user?.billingAccount ?? null;

  const { canEditBillingPlan } = useFeatureAccess();
  const queryClient = useQueryClient();

  const paymentMethodQuery = useQuery({
    queryKey: getQueryKey(billingAccount?.id),
    queryFn: async () =>
      await apiClient.billing.getPaymentMethod(billingAccount!.id),
    enabled: canEditBillingPlan && Boolean(billingAccount?.id),
    refetchOnWindowFocus: false,
  });

  const removePaymentMethodMutation = useMutation({
    mutationFn: async () => {
      if (!billingAccount?.id) throw new Error("No billing account");
      return apiClient.billing.removePaymentMethod(billingAccount.id);
    },
    onSuccess: () => Events.removePaymentMethod(),
    onSettled: () => queryClient.invalidateQueries({ queryKey: ["billing"] }),
  });

  const createPaymentPortalLink = useMutation<
    { url: string },
    Error,
    { returnUrl: string; flow: "payment" | "address" }
  >({
    mutationFn: async ({ returnUrl, flow }) => {
      if (!billingAccount?.id) throw new Error("No billing account");
      return apiClient.billing.createPaymentPortalLink(billingAccount.id, {
        returnUrl,
        flow,
      });
    },
  });

  const paymentMethod = paymentMethodQuery.data?.paymentMethod;
  const paymentMethodIsLoading = paymentMethodQuery.isInitialLoading;
  const hasPaymentMethod = !paymentMethodIsLoading && Boolean(paymentMethod);

  return {
    hasPaymentMethod,
    paymentMethod,
    paymentMethodIsLoading,
    isError: paymentMethodQuery.isError,
    createPaymentPortalLink,
    removePaymentMethod: removePaymentMethodMutation,
  };
};

export default usePaymentMethod;
